// src/pages/admin/AdminAttendance.jsx
// ============================================================
// Three-tab attendance view for admins:
//   1. Today        — live view of today (original behaviour)
//   2. By Date      — pick any past/future date, see + download
//   3. Monthly      — pick month/year, per-employee summary + download
// ============================================================

import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  useAllTodayAttendance,
  useAttendanceByDate,
  useAllMonthlyAttendance,
  useAllUsers,
  useAllLeaveRequests,
  formatTime,
  getTodayStr,
} from "../../hooks/useFirestore";
import {
  useHolidays,
  useHolidaysInMonth,
  isHoliday,
  resolveEmployeeStatus,
  useAllShiftAssignments,
  useShifts,
} from "../../hooks/useFirestoreExtensions";
import { isPresent } from "../../utils/attendanceStatus";
import { ADMIN_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { calculateEmployeeStatsForRange } from "../../utils/statusCalculations";

// ──Utility: CSV download ──────────────────────────────────
function downloadCSV(filename, headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Utility: count Mon–Fri working days in a month, excluding holidays ──
function getWorkDaysInMonth(year, month, holidayDates = new Set()) {
  const now = new Date();
  const isCurrentMonth =
    now.getFullYear() === year && now.getMonth() + 1 === month;
  const lastDay = isCurrentMonth
    ? now.getDate()
    : new Date(year, month, 0).getDate();

  let count = 0;
  for (let d = 1; d <= lastDay; d++) {
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const day = new Date(year, month - 1, d).getDay();
    if (day !== 0 && day !== 6 && !holidayDates.has(dateStr)) count++;
  }
  return count;
}

// ── Utility: format Timestamp → "HH:MM" text for CSV ─────
function tsToStr(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

// ═══════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════
export default function AdminAttendance() {
  const [tab, setTab] = useState("today");

  // Shared data
  const { data: users, loading: uLoad } = useAllUsers();
  const { data: holidays } = useHolidays();
  const { data: allLeaves } = useAllLeaveRequests();
  const employees = users.filter(
    (u) => u.role === "employee" || u.role === "lead",
  );

  // ── Tab 1: Today ─────────────────────────────────────────
  const { data: todayAtt, loading: aLoad } = useAllTodayAttendance();
  const { data: assignments } = useAllShiftAssignments();
  const { data: shifts } = useShifts();
  
  const [filterDept, setFilterDept] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterShift, setFilterShift] = useState("all");
  const [selectedStatusCard, setSelectedStatusCard] = useState(null);

  function handleStatusCardClick(statusKey) {
    setSelectedStatusCard((prev) => (prev === statusKey ? null : statusKey));
  }

  const todayStr = getTodayStr();
  const todayHoliday = isHoliday(todayStr, holidays);

  const depts = useMemo(
    () => [...new Set(users.map((u) => u.department))].filter(Boolean).sort(),
    [users],
  );

  const employeeShiftMap = useMemo(() => {
    const map = {};
    if (!assignments || !shifts) return map;
    const activeAssignments = assignments.filter(a => a.status === "APPROVED");
    for (const a of activeAssignments) {
      const shift = shifts.find(s => s.id === a.shiftId);
      if (shift) map[a.userId] = shift;
    }
    return map;
  }, [assignments, shifts]);

  // Build today rows using resolveEmployeeStatus (holiday + leave aware)
  const todayRows = useMemo(() => {
    const attMap = {};
    for (const a of todayAtt) attMap[a.userId] = a;

    return employees.map((emp) => {
      const att = attMap[emp.uid] || null;
      const leaves = allLeaves.filter(
        (l) => l.userId === emp.uid && l.status === "approved",
      );
      const resolved = resolveEmployeeStatus(
        emp,
        att,
        leaves,
        todayHoliday,
        todayStr,
      );

      return att
        ? { ...att, _hasRecord: true, _resolved: resolved, _shift: employeeShiftMap[emp.uid] }
        : {
            userId: emp.uid,
            displayName: emp.displayName,
            department: emp.department,
            employeeId: emp.employeeId,
            status: resolved.status,
            mode: "—",
            checkIn: null,
            checkOut: null,
            hoursWorked: 0,
            _hasRecord: false,
            _resolved: resolved,
            _shift: employeeShiftMap[emp.uid]
          };
    });
  }, [todayAtt, employees, allLeaves, todayHoliday, todayStr, employeeShiftMap]);

  const todayFiltered = useMemo(
    () =>
      todayRows.filter((r) => {
        const d = filterDept === "all" || r.department === filterDept;
        const resolvedStatus = r._resolved?.status || r.status;
        const s = filterStatus === "all" || resolvedStatus === filterStatus;
        
        let shiftMatch = true;
        if (filterShift !== "all") {
          if (filterShift === "unassigned") {
            shiftMatch = !r._shift;
          } else {
            if (!r._shift) {
              shiftMatch = false;
            } else {
              const sName = (r._shift.name || "").toLowerCase();
              const f = filterShift; // "morning", "afternoon", "night", "general"
              shiftMatch = sName.includes(f) || 
                           (f === "afternoon" && sName.includes("evening")) || 
                           (f === "general" && sName.includes("custom")) ||
                           (sName === f + " shift");
            }
          }
        }
        return d && s && shiftMatch;
      }),
    [todayRows, filterDept, filterStatus, filterShift],
  );

  // Download today's CSV
  function downloadTodayCSV() {
    const today = getTodayStr();
    const headers = [
      "Name",
      "Employee ID",
      "Department",
      "Status",
      "Punch-In",
      "Punch-Out",
      "Hours",
      "Mode",
      "Late",
      "Late By (min)",
    ];
    const rows = todayFiltered.map((r) => [
      r.displayName,
      r.employeeId,
      r.department,
      r.status,
      tsToStr(r.checkIn),
      tsToStr(r.checkOut),
      r.hoursWorked > 0 ? r.hoursWorked : "—",
      r.mode || "—",
      r.isLate ? "Yes" : r._hasRecord ? "No" : "—",
      r.isLate ? r.lateByMinutes : "—",
    ]);
    downloadCSV(`attendance_${today}.csv`, headers, rows);
  }
  function downloadTodayExcel() {
  const data = todayFiltered.map((r) => ({
    Name: r.displayName,
    EmployeeID: r.employeeId,
    Department: r.department,
    Status: r.status,
    CheckIn: tsToStr(r.checkIn),
    CheckOut: tsToStr(r.checkOut),
    Hours: r.hoursWorked > 0 ? r.hoursWorked : "—",
    Mode: r.mode || "—",
    Late: r.isLate ? "Yes" : "No",
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Attendance"
  );

  XLSX.writeFile(
    workbook,
    `attendance_${todayStr}.xlsx`
  );
}

function downloadTodayPDF() {
  const doc = new jsPDF();

  doc.text("Attendance Report", 14, 15);

  autoTable(doc, {
    startY: 25,
    head: [[
      "Name",
      "Employee ID",
      "Department",
      "Status",
      "Punch-In",
      "Punch-Out",
      "Hours",
      "Mode",
    ]],
    body: todayFiltered.map((r) => [
      r.displayName,
      r.employeeId,
      r.department,
      r.status,
      tsToStr(r.checkIn),
      tsToStr(r.checkOut),
      r.hoursWorked > 0 ? r.hoursWorked : "—",
      r.mode || "—",
    ]),
  });

  doc.save(`attendance_${todayStr}.pdf`);
}
  // ── Tab 2: By Date ───────────────────────────────────────
  const [selectedDate, setSelectedDate] = useState(getTodayStr());
  const { data: dateAtt, loading: dLoad } = useAttendanceByDate(selectedDate);

  const dateRows = useMemo(() => {
    const presentIds = new Set(dateAtt.map((a) => a.userId));
    const holiday = isHoliday(selectedDate, holidays);

    return [
      ...dateAtt.map((a) => {
        const emp = employees.find((e) => e.uid === a.userId) || {};
        const empLeaves = allLeaves.filter(
          (l) => l.userId === a.userId && l.fromDate <= selectedDate && l.toDate >= selectedDate
        );
        const resolved = resolveEmployeeStatus(emp, a, empLeaves, holiday, selectedDate);
        return { ...a, _hasRecord: true, _resolved: resolved };
      }),
      ...employees
        .filter((u) => !presentIds.has(u.uid))
        .map((u) => {
          const empLeaves = allLeaves.filter(
            (l) => l.userId === u.uid && l.fromDate <= selectedDate && l.toDate >= selectedDate
          );
          const resolved = resolveEmployeeStatus(u, null, empLeaves, holiday, selectedDate);
          return {
            userId: u.uid,
            displayName: u.displayName,
            department: u.department,
            employeeId: u.employeeId,
            status: resolved.status,
            mode: "—",
            checkIn: null,
            checkOut: null,
            hoursWorked: 0,
            _hasRecord: false,
            _resolved: resolved,
          };
        }),
    ];
  }, [dateAtt, employees, holidays, allLeaves, selectedDate]);

  const dateSummary = useMemo(
    () => ({
      present: dateRows.filter(isPresent).length,
      absent: dateRows.filter((r) => {
        if (isPresent(r)) return false;
        const status = r._resolved?.status || r.status;
        return status === "absent" || status === "on_leave";
      }).length,
      wfh: dateRows.filter((r) => r.mode === "remote").length,
      late: dateRows.filter((r) => r.isLate).length,
    }),
    [dateRows],
  );

  function downloadDateCSV() {
    const headers = [
      "Name",
      "Employee ID",
      "Department",
      "Status",
      "Punch-In",
      "Punch-Out",
      "Hours",
      "Mode",
      "Late",
      "Late By (min)",
    ];
    const rows = dateRows.map((r) => [
      r.displayName,
      r.employeeId,
      r.department,
      r.status,
      tsToStr(r.checkIn),
      tsToStr(r.checkOut),
      r.hoursWorked > 0 ? r.hoursWorked : "—",
      r.mode || "—",
      r.isLate ? "Yes" : r._hasRecord ? "No" : "—",
      r.isLate ? r.lateByMinutes : "—",
    ]);
    downloadCSV(`attendance_${selectedDate}.csv`, headers, rows);
  }

  // ── Tab 3: Monthly Report ────────────────────────────────
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);

  const { data: monthAtt, loading: mLoad } = useAllMonthlyAttendance(
    selYear,
    selMonth,
  );

  const { data: monthHolidays } = useHolidaysInMonth(selYear, selMonth);

  const monthlyReport = useMemo(() => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const mm = String(selMonth).padStart(2, "0");
    const startDate = `${selYear}-${mm}-01`;
    const isCurrentMonth = today.getFullYear() === selYear && today.getMonth() + 1 === selMonth;
    const lastDay = isCurrentMonth
      ? today.getDate()
      : new Date(selYear, selMonth, 0).getDate();
    const endDate = `${selYear}-${mm}-${String(lastDay).padStart(2, "0")}`;

    return employees.map((emp) => {
      const records = monthAtt.filter((a) => a.userId === emp.uid);
      const empLeaves = allLeaves.filter((l) => l.userId === emp.uid);
      const wfhDays = records.filter((a) => a.mode === "remote").length;
      const lateDays = records.filter((a) => a.isLate).length;
      const totalHours = records.reduce((s, a) => s + (a.hoursWorked || 0), 0);

      const stats = calculateEmployeeStatsForRange({
        attendanceRecords: records,
        leaveRequests: empLeaves,
        holidays: monthHolidays,
        startDate,
        endDate,
        todayStr,
      });

      return {
        displayName: emp.displayName,
        employeeId: emp.employeeId || "—",
        department: emp.department || "—",
        presentDays: stats.present,
        absentDays: stats.absent,
        leaveDays: stats.leaves,
        wfhDays,
        lateDays,
        totalHours: Math.round(totalHours * 10) / 10,
        attendRate: stats.rate,
      };
    });
  }, [employees, monthAtt, allLeaves, monthHolidays, selYear, selMonth]);

  const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const yearOptions = useMemo(() => {
    const y = [];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= 2023; i--) y.push(i);
    return y;
  }, []);

  function downloadMonthlyCSV() {
    const label = `${MONTHS[selMonth - 1]}_${selYear}`;
    const headers = [
      "Name",
      "Employee ID",
      "Department",
      "Days Present",
      "Days Absent",
      "Leaves Taken",
      "WFH Days",
      "Late Days",
      "Total Hours",
      "Attendance %",
    ];
    const rows = monthlyReport.map((r) => [
      r.displayName,
      r.employeeId,
      r.department,
      r.presentDays,
      r.absentDays,
      r.leaveDays,
      r.wfhDays,
      r.lateDays,
      r.totalHours,
      `${r.attendRate}%`,
    ]);
    downloadCSV(`monthly_attendance_${label}.csv`, headers, rows);
  }

  // ── Shared sub-components ────────────────────────────────
  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      <div className="page-header">
        <div>
          <h1 className="page-title">Attendance</h1>
          <p className="page-subtitle">{todayLabel}</p>
        </div>
      </div>

      {/* ── Tab bar ──────────────────────────────────────── */}
      <div className="att-tab-bar">
  {[
    {
      key: "today",
      label: (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            width="18"
            height="18"
            style={{ marginRight: "6px" }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
            />
          </svg>
          Today
        </>
      ),
    },

    {
      key: "date",
      label: (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            width="18"
            height="18"
            style={{ marginRight: "6px" }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
            />
          </svg>
          By Date
        </>
      ),
    },

    {
      key: "monthly",
      label: (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            width="20"
            height="20"
            style={{ marginRight: "6px" }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z"
            />
          </svg>
          Monthly Report
        </>
      ),
    },
  ].map(({ key, label }) => (
    <button
      key={key}
      className={`att-tab-btn ${tab === key ? "att-tab-btn--active" : ""}`}
      onClick={() => setTab(key)}
    >
      {label}
    </button>
  ))}
</div>

      {/* ════════════════════════════════════════════════════
          TAB 1: TODAY
          ════════════════════════════════════════════════════ */}
      {tab === "today" && (
        <>
          {/* Summary pills */}
          <div className="attendance-summary-row">
            <SummaryChip
              label="On Break"
              count={todayFiltered.filter((r) => r._resolved?.status === "on_break").length}
              color="blue"
              isActive={selectedStatusCard === "on_break"}
              onClick={() => handleStatusCardClick("on_break")}
            />
            <SummaryChip
              label="Active"
              count={todayFiltered.filter((r) => r._resolved?.status === "active").length}
              color="green"
              isActive={selectedStatusCard === "active"}
              onClick={() => handleStatusCardClick("active")}
            />
            <SummaryChip
              label="Absent"
              count={todayFiltered.filter((r) => r._resolved?.status === "absent").length}
              color="red"
              isActive={selectedStatusCard === "absent"}
              onClick={() => handleStatusCardClick("absent")}
            />
            <SummaryChip
              label="On Leave"
              count={todayFiltered.filter((r) => r._resolved?.status === "on_leave").length}
              color="amber"
              isActive={selectedStatusCard === "on_leave"}
              onClick={() => handleStatusCardClick("on_leave")}
            />
            <SummaryChip
              label="Remote"
              count={todayFiltered.filter((r) => r.mode === "remote").length}
              color="blue"
              isActive={selectedStatusCard === "remote"}
              onClick={() => handleStatusCardClick("remote")}
            />
            <SummaryChip
              label="Late"
              count={todayFiltered.filter((r) => r.isLate).length}
              color="amber"
              isActive={selectedStatusCard === "late"}
              onClick={() => handleStatusCardClick("late")}
            />
            {todayHoliday && (
              <div
                style={{
                  background: "var(--purple-bg, #f3e8ff)",
                  borderRadius: "var(--radius)",
                  padding: "0.5rem 1rem",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  color: "var(--purple, #7c3aed)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                }}
              >
                <div
  style={{
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
  }}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className="size-6"
    style={{
      width: "1rem",
      height: "1rem",
    }}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
    />
  </svg>

  <span>Holiday: {todayHoliday.name}</span>
</div>
              </div>
            )}
          </div>

          {/* ── Status Drill-Down Panel ── */}
          {selectedStatusCard && (() => {
            const statusConfig = {
              on_break:  { label: "On Break",  color: "#1b4b31", bg: "#eaf4ee", border: "#a7f3d0" },
              active:    { label: "Active",    color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
              absent:    { label: "Absent",    color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
              on_leave:  { label: "On Leave",  color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
              remote:    { label: "Remote",    color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
              late:      { label: "Late",      color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
            };
            const cfg = statusConfig[selectedStatusCard];
            const drillRows = todayFiltered.filter((r) => {
              if (selectedStatusCard === "remote") return r.mode === "remote";
              if (selectedStatusCard === "late")   return r.isLate;
              return r._resolved?.status === selectedStatusCard;
            });
            return (
              <div style={{
                marginTop: "0.75rem",
                border: `1.5px solid ${cfg.border}`,
                borderRadius: "12px",
                background: cfg.bg,
                overflow: "hidden",
                animation: "fadeInDown 0.2s ease",
              }}>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "0.75rem 1.1rem",
                  borderBottom: `1px solid ${cfg.border}`,
                  background: cfg.bg,
                }}>
                  <span style={{ fontWeight: 700, fontSize: "0.95rem", color: cfg.color }}>
                    {cfg.label} — {drillRows.length} Employee{drillRows.length !== 1 ? "s" : ""}
                  </span>
                  <button
                    onClick={() => setSelectedStatusCard(null)}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: cfg.color, fontWeight: 700, fontSize: "1.1rem", lineHeight: 1,
                      padding: "0 4px",
                    }}
                    title="Close"
                  >✕</button>
                </div>
                {drillRows.length === 0 ? (
                  <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.9rem" }}>
                    No employees found for this status.
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                      <thead>
                        <tr style={{ background: cfg.border }}>
                          <th style={{ padding: "0.55rem 0.9rem", textAlign: "left", fontWeight: 700, color: cfg.color, whiteSpace: "nowrap" }}>Employee</th>
                          <th style={{ padding: "0.55rem 0.9rem", textAlign: "left", fontWeight: 700, color: cfg.color, whiteSpace: "nowrap" }}>Employee ID</th>
                          <th style={{ padding: "0.55rem 0.9rem", textAlign: "left", fontWeight: 700, color: cfg.color, whiteSpace: "nowrap" }}>Department</th>
                          <th style={{ padding: "0.55rem 0.9rem", textAlign: "left", fontWeight: 700, color: cfg.color, whiteSpace: "nowrap" }}>Designation</th>
                          <th style={{ padding: "0.55rem 0.9rem", textAlign: "left", fontWeight: 700, color: cfg.color, whiteSpace: "nowrap" }}>Punch-In</th>
                          <th style={{ padding: "0.55rem 0.9rem", textAlign: "left", fontWeight: 700, color: cfg.color, whiteSpace: "nowrap" }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drillRows.map((r, i) => {
                          const empProfile = users.find((u) => u.uid === r.userId);
                          return (
                            <tr key={r.userId + i} style={{ borderBottom: `1px solid ${cfg.border}`, background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.5)" }}>
                              <td style={{ padding: "0.5rem 0.9rem", fontWeight: 600 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                  <div style={{
                                    width: 28, height: 28, borderRadius: "50%",
                                    background: cfg.color, color: "#fff",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: "0.7rem", fontWeight: 700, flexShrink: 0,
                                  }}>
                                    {r.displayName?.slice(0, 2).toUpperCase()}
                                  </div>
                                  {r.displayName}
                                </div>
                              </td>
                              <td style={{ padding: "0.5rem 0.9rem", fontFamily: "var(--font-mono)", color: "var(--text-secondary)" }}>{r.employeeId || "—"}</td>
                              <td style={{ padding: "0.5rem 0.9rem" }}>{r.department || "—"}</td>
                              <td style={{ padding: "0.5rem 0.9rem", color: "var(--text-secondary)" }}>{empProfile?.designation || "—"}</td>
                              <td style={{ padding: "0.5rem 0.9rem", fontFamily: "var(--font-mono)" }}>{formatTime(r.checkIn) || "—"}</td>
                              <td style={{ padding: "0.5rem 0.9rem" }}>
                                <StatusPill resolved={r._resolved} fallback={r.status} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Filters + download */}
          <div className="table-filters" style={{ marginTop: "20px" }}>
            <select
              className="filter-select"
              value={filterDept}
              onChange={(e) => setFilterDept(e.target.value)}
            >
              <option value="all">All Departments</option>
              {depts.map((d) => (
                <option key={d}>{d}</option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="on_break">On Break</option>
              <option value="absent">Absent</option>
              <option value="on_leave">On Leave</option>
              <option value="holiday">Holiday</option>
              <option value="weekend">Weekend</option>
            </select>
            <select
              className="filter-select"
              value={filterShift}
              onChange={(e) => setFilterShift(e.target.value)}
            >
              <option value="all">All Shifts</option>
              <option value="morning">Morning Shift</option>
              <option value="afternoon">Afternoon Shift</option>
              <option value="night">Night Shift</option>
              <option value="general">General Shift</option>
              <option value="unassigned">Unassigned</option>
            </select>
            <div className="download-dropdown">
  <button
    className="btn-download"
    disabled={todayFiltered.length === 0}
  >
    ⬇ Download Report
  </button>

  <div className="download-dropdown-content">
    <button onClick={downloadTodayCSV}>
      Download CSV
    </button>

    <button onClick={downloadTodayExcel}>
      Download Excel
    </button>

    <button onClick={downloadTodayPDF}>
      Download PDF
    </button>
  </div>
</div>
          </div>

          {aLoad || uLoad ? (
            <div className="card-loading">Loading attendance…</div>
          ) : (
            <AttTable rows={todayFiltered} />
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════
          TAB 2: BY DATE
          ════════════════════════════════════════════════════ */}
      {tab === "date" && (
        <>
          <div className="report-controls">
            <div className="report-controls-left">
              <label className="report-label">Select Date</label>
              <input
                type="date"
                className="report-date-input"
                value={selectedDate}
                max={getTodayStr()}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            <button
              className="btn-download"
              onClick={downloadDateCSV}
              disabled={!selectedDate || dLoad || dateRows.length === 0}
            >
              ⬇ Download CSV
            </button>
          </div>

          {selectedDate && (
            <>
              <div
                className="attendance-summary-row"
                style={{ marginTop: "1rem" }}
              >
                <SummaryChip
                  label="Present"
                  count={dateSummary.present}
                  color="green"
                />
                <SummaryChip
                  label="Absent"
                  count={dateSummary.absent}
                  color="red"
                />
                <SummaryChip
                  label="Remote"
                  count={dateSummary.wfh}
                  color="blue"
                />
                <SummaryChip
                  label="Late"
                  count={dateSummary.late}
                  color="amber"
                />
              </div>

              {dLoad || uLoad ? (
                <div className="card-loading">
                  Loading attendance for {selectedDate}…
                </div>
              ) : (
                <AttTable rows={dateRows} />
              )}
            </>
          )}

          {!selectedDate && (
            <div className="card-empty" style={{ marginTop: "2rem" }}>
              Pick a date above to view attendance.
            </div>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════
          TAB 3: MONTHLY REPORT
          ════════════════════════════════════════════════════ */}
      {tab === "monthly" && (
        <>
          <div className="report-controls">
            <div className="report-controls-left">
              <label className="report-label">Month</label>
              <select
                className="report-select"
                value={selMonth}
                onChange={(e) => setSelMonth(Number(e.target.value))}
              >
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>

              <label className="report-label" style={{ marginLeft: "0.75rem" }}>
                Year
              </label>
              <select
                className="report-select"
                value={selYear}
                onChange={(e) => setSelYear(Number(e.target.value))}
              >
                {yearOptions.map((y) => (
                  <option key={y}>{y}</option>
                ))}
              </select>
            </div>

            <button
              className="btn-download"
              onClick={downloadMonthlyCSV}
              disabled={mLoad || monthlyReport.length === 0}
            >
              ⬇ Download CSV
            </button>
          </div>

          {/* Month summary KPIs */}
          {!mLoad && !uLoad && (
            <div className="kpi-grid" style={{ marginTop: "1rem" }}>
              <KpiChip
  label="Total Employees"
  value={monthlyReport.length}
  color="blue"
  icon={
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="18"
      height="18"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
      />
    </svg>
  }
/>

<KpiChip
  label="Avg Attendance"
  value={
    monthlyReport.length > 0
      ? `${Math.round(
          monthlyReport.reduce((s, r) => s + r.attendRate, 0) /
            monthlyReport.length
        )}%`
      : "—"
  }
  color="green"
  icon={
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="18"
      height="18"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z"
      />
    </svg>
  }
/>

<KpiChip
  label="Work Days"
  value={getWorkDaysInMonth(selYear, selMonth)}
  color="amber"
  icon={
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="18"
      height="18"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
      />
    </svg>
  }
/>

<KpiChip
  label="Total Late"
  value={monthlyReport.reduce((s, r) => s + r.lateDays, 0)}
  color="red"
  icon={
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="18"
      height="18"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  }
/>
            </div>
          )}

          {mLoad || uLoad ? (
            <div className="card-loading">Loading monthly report…</div>
          ) : monthlyReport.length === 0 ? (
            <div className="card-empty">No employees found.</div>
          ) : (
            <div className="data-table-wrap" style={{ marginTop: "1rem" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>ID</th>
                    <th>Department</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Leaves Taken</th>
                    <th>Remote</th>
                    <th>Late Days</th>
                    <th>Hours Worked</th>
                    <th>Attendance %</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyReport.map((r, i) => (
                    <tr key={i}>
                      <td>
                        <div className="table-user-cell">
                          <div className="table-avatar">
                            {r.displayName?.slice(0, 2).toUpperCase()}
                          </div>
                          {r.displayName}
                        </div>
                      </td>
                      <td
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "0.8rem",
                        }}
                      >
                        {r.employeeId}
                      </td>
                      <td>{r.department}</td>
                      <td>
                        <span className="status-pill status-pill--green">
                          {r.presentDays}d
                        </span>
                      </td>
                      <td>
                        {r.absentDays > 0 ? (
                          <span className="status-pill status-pill--red">
                            {r.absentDays}d
                          </span>
                        ) : (
                          <span className="status-pill status-pill--green">
                            0d
                          </span>
                        )}
                      </td>
                      <td>
                        {r.leaveDays > 0 ? (
                          <span className="status-pill status-pill--amber">
                            {r.leaveDays}d
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {r.wfhDays > 0 ? (
                          <span className="status-pill status-pill--blue">
                            {r.wfhDays}d
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {r.lateDays > 0 ? (
                          <span className="status-pill status-pill--amber">
                            {r.lateDays}d
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={{ fontFamily: "var(--font-mono)" }}>
                        {r.totalHours}h
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <div className="month-att-bar">
                            <div
                              className="month-att-fill"
                              style={{
                                width: `${r.attendRate}%`,
                                background:
                                  r.attendRate >= 90
                                    ? "var(--green)"
                                    : r.attendRate >= 70
                                      ? "var(--amber)"
                                      : "var(--red)",
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: "0.8rem",
                              fontWeight: 700,
                              minWidth: "36px",
                            }}
                          >
                            {r.attendRate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Layout>
  );
}

// ── Sub-components ─────────────────────────────────────────

function AttTable({ rows }) {
  if (rows.length === 0) {
    return <div className="card-empty">No records match your filters.</div>;
  }
  return (
    <div className="data-table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Employee</th>
            <th>ID</th>
            <th>Department</th>
            <th>Status</th>
            <th>Punch-In</th>
            <th>Punch-Out</th>
            <th>Hours</th>
            <th>Mode</th>
            <th>Breaks</th>
            <th>Late?</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.userId + i}>
              <td>
                <div className="table-user-cell">
                  <div className="table-avatar">
                    {r.displayName?.slice(0, 2).toUpperCase()}
                  </div>
                  {r.displayName}
                </div>
              </td>
              <td>{r.employeeId}</td>
              <td>{r.department}</td>
              <td>
                <StatusPill resolved={r._resolved} fallback={r.status} />
              </td>
              <td>{formatTime(r.checkIn)}</td>
              <td>{formatTime(r.checkOut)}</td>
              <td>{r.hoursWorked > 0 ? `${r.hoursWorked}h` : "—"}</td>
              <td style={{ textTransform: "capitalize" }}>{r.mode || "—"}</td>
              <td>
                {(r.breaks || []).length === 0 ? (
                  "—"
                ) : (
                  <div style={{ fontSize: "0.75rem", lineHeight: 1.5 }}>
                    {(r.breaks || []).map((b, bi) => (
                      <div key={bi} style={{ whiteSpace: "nowrap" }}>
                        <span style={{ fontWeight: 600 }}>
                          {b.breakType === "tea1" ? "Tea1" : b.breakType === "tea2" ? "Tea2" : "Lunch"}
                        </span>{" "}
                        {tsToStr(b.startTime)}
                        {b.endTime ? (
                          <span style={{ color: "#6c757d" }}>
                            {" → "}{tsToStr(b.endTime)}{" "}
                            <span style={{ color: b.duration > (b.breakType === "lunch" ? 60 : 15) ? "#e74c3c" : "#2ecc71" }}>
                              ({b.duration}m)
                              {b.duration > (b.breakType === "lunch" ? 60 : 15) && ` - Excess: ${b.duration - (b.breakType === "lunch" ? 60 : 15)}m`}
                            </span>
                          </span>
                        ) : (
                          <span style={{ color: "#f39c12", fontWeight: 700 }}> (Active)</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </td>
              <td>
                {r.isLate ? (
                  <span className="status-pill status-pill--red">
                    +{r.lateByMinutes}m
                  </span>
                ) : r._hasRecord ? (
                  <span className="status-pill status-pill--green">
                    On time
                  </span>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummaryChip({ label, count, color, onClick, isActive }) {
  return (
    <div
      className={`summary-chip summary-chip--${color}`}
      onClick={onClick}
      title={`Click to see ${label} employees`}
      style={{
        cursor: onClick ? "pointer" : "default",
        outline: isActive ? `2px solid currentColor` : "2px solid transparent",
        outlineOffset: "2px",
        transform: isActive ? "scale(1.04)" : "scale(1)",
        transition: "transform 0.15s, outline 0.15s",
        boxShadow: isActive ? "0 4px 14px rgba(0,0,0,0.12)" : undefined,
      }}
    >
      <span className="summary-chip-count">{count}</span>
      <span className="summary-chip-label">{label}</span>
    </div>
  );
}

function KpiChip({ label, value, color, icon }) {
  return (
    <div className={`kpi-card kpi-card--${color}`}>
      <div className="kpi-icon">{icon}</div>
      <div className="kpi-body">
        <span className="kpi-value">{value}</span>
        <span className="kpi-label">{label}</span>
      </div>
    </div>
  );
}

function StatusPill({ resolved, fallback }) {
  // Priority: use the resolved smart status first
  const status = resolved?.status || fallback;
  const label = resolved?.label || fallback;

  const colorMap = {
    active: "green",
    absent: "red",
    on_leave: "amber",
    on_break: "blue",
    completed: "blue",
    holiday: "purple",
    weekend: "gray",
    present: "green",
    wfh: "blue",
    leave: "amber",
    pending_leave: "amber",
    rejected_leave: "gray",
  };

  return (
    <span className={`status-pill status-pill--${colorMap[status] || "gray"}`}>
      {label || status}
    </span>
  );
}